/**
 * ImageModal Component
 * Full-screen image viewer with zoom, pan, and navigation
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { formatDateTime } from '../utils/formatDateTime';
import {
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';

export default function ImageModal({
  isOpen,
  onClose,
  imageUrl,
  title,
  subtitle,
  metadata,
  images = [], // For navigation between multiple images
  currentIndex = 0,
  onNavigate,
}) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Reset state when image changes
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageLoaded(false);
    setImageError(false);
  }, [imageUrl]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (images.length > 1 && onNavigate) {
            onNavigate(Math.max(0, currentIndex - 1));
          }
          break;
        case 'ArrowRight':
          if (images.length > 1 && onNavigate) {
            onNavigate(Math.min(images.length - 1, currentIndex + 1));
          }
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          handleResetZoom();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, images.length, currentIndex, onNavigate, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Use non-passive wheel listener to enable preventDefault for zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isOpen) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((prev) => Math.max(0.5, Math.min(4, prev + delta)));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [isOpen]);

  const handleMouseDown = useCallback((e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      // Use relative URL to allow Nginx/Vite proxying to handle it
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alert-snapshot-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, [imageUrl]);

  const getFullImageUrl = (url) => {
    if (!url) return '';
    // If it's already an absolute URL or data URI, return as is
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    // Otherwise prepend backend URL for relative paths
    return `${BACKEND_URL}${url}`;
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Image viewer'}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black/80 to-transparent">
          <div>
            {title && (
              <h3 className="text-lg font-semibold text-white">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-white/70">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
              <button
                onClick={handleZoomOut}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Zoom out (-)"
                aria-label="Zoom out"
              >
                <MagnifyingGlassMinusIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <span className="text-sm text-white/80 min-w-[4rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Zoom in (+)"
                aria-label="Zoom in"
              >
                <MagnifyingGlassPlusIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <button
                onClick={handleResetZoom}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Reset zoom (0)"
                aria-label="Reset zoom"
              >
                <ArrowsPointingOutIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Download button */}
            <button
              onClick={handleDownload}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Download image"
              aria-label="Download image"
            >
              <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Close (Esc)"
              aria-label="Close image viewer"
            >
              <XMarkIcon className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Image Container */}
        <div
          ref={containerRef}
          className={clsx(
            'flex-1 flex items-center justify-center overflow-hidden',
            zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in',
            isDragging && 'cursor-grabbing'
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {imageError ? (
            <div className="text-center text-white/60">
              <p className="text-lg">Failed to load image</p>
              <p className="text-sm mt-2">{imageUrl}</p>
            </div>
          ) : (
            <img
              ref={imageRef}
              src={getFullImageUrl(imageUrl)}
              alt={title || 'Alert snapshot'}
              className={clsx(
                'max-w-full max-h-full object-contain transition-opacity duration-300',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              draggable={false}
            />
          )}
        </div>

        {/* Navigation arrows for multiple images */}
        {images.length > 1 && onNavigate && (
          <>
            <button
              onClick={() => onNavigate(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className={clsx(
                'absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white transition-all',
                currentIndex === 0
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-black/70 hover:scale-110'
              )}
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <button
              onClick={() => onNavigate(Math.min(images.length - 1, currentIndex + 1))}
              disabled={currentIndex === images.length - 1}
              className={clsx(
                'absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white transition-all',
                currentIndex === images.length - 1
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-black/70 hover:scale-110'
              )}
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}

        {/* Metadata footer */}
        {metadata && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-6 py-4">
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              {metadata.confidence && (
                <span>
                  Confidence: <span className="text-white font-medium">{(metadata.confidence * 100).toFixed(0)}%</span>
                </span>
              )}
              {metadata.detectedAt && (
                <span>
                  Detected: <span className="text-white font-medium">{formatDateTime(metadata.detectedAt)}</span>
                </span>
              )}
              {metadata.camera && (
                <span>
                  Camera: <span className="text-white font-medium">{metadata.camera}</span>
                </span>
              )}
              {metadata.type && (
                <span>
                  Type: <span className="text-white font-medium">{metadata.type?.replace(/_/g, ' ')}</span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

/**
 * AlertThumbnail Component
 * Thumbnail image with click to expand and lazy loading
 */
export function AlertThumbnail({
  imageUrl,
  alt,
  size = 'md',
  onClick,
  className,
  showPlaceholder = true,
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-16',
    lg: 'w-32 h-24',
    xl: 'w-48 h-36',
  };

  if (!imageUrl || error) {
    if (!showPlaceholder) return null;
    return (
      <div
        ref={containerRef}
        className={clsx(
          'rounded-lg bg-slate-100 flex items-center justify-center text-slate-400',
          sizeClasses[size],
          className
        )}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative rounded-lg overflow-hidden bg-slate-100 cursor-pointer group',
        sizeClasses[size],
        className
      )}
      onClick={onClick}
    >
      {(!loaded || !isVisible) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
        </div>
      )}
      {isVisible && (
        <img
          src={getFullUrl(imageUrl)}
          alt={alt || 'Alert thumbnail'}
          loading="lazy"
          decoding="async"
          className={clsx(
            'w-full h-full object-cover transition-all duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
            'group-hover:scale-105'
          )}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <MagnifyingGlassPlusIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}
